import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleWhereToHangoutVancouverFirstDate() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "where-hangout-for-a-first-date-in-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext() || {};
  const ref_data = articles
    ? articles.find((art) => art.slug === ref_id)
    : null;

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "outdoor-scenic": "Outdoor Scenic Spots",
    "cozy-cafes": "Cozy Cafés & Desserts",
    "casual-bars": "Casual Bars & Breweries",
    "fun-activities": "Fun Activities & Attractions",
    "adventurous-outings": "Adventurous Outings",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://destinationvancouver.com",
      label: "Destination Vancouver (Official Tourism)",
    },
    {
      url: "https://vancouver.ca",
      label: "City of Vancouver (Parks & Information)",
    },
    {
      url: "https://hellobc.com/vancouver",
      label: "HelloBC – Vancouver Travel Guide",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Each location's "id" matches the relevant section. If multiple spots appear
  // in the same section, we can differentiate them with numbered suffixes, e.g., "outdoor-scenic-1".
  // We keep lat/lng to 5 decimals as required.
  const locations = [
    // Outdoor Scenic
    {
      id: "stanley-park-seawall",
      lat: 49.30000,
      lng: -123.14000,
      popupText: "Stanley Park Seawall",
    },
    {
      id: "queen-elizabeth-park",
      lat: 49.24192,
      lng: -123.11262,
      popupText: "Queen Elizabeth Park",
    },
    {
      id: "spanish-banks",
      lat: 49.27694,
      lng: -123.21611,
      popupText: "Spanish Banks Beach Park",
    },
    // Cozy Cafés
    {
      id: "49th-parallel-cafe",
      lat: 49.25850,
      lng: -123.10150,
      popupText: "49th Parallel Café & Lucky’s Doughnuts",
    },
    {
      id: "rain-or-shine",
      lat: 49.26821,
      lng: -123.15568,
      popupText: "Rain or Shine Ice Cream (Kitsilano)",
    },
    // Casual Bars
    {
      id: "tap-and-barrel-olympic",
      lat: 49.27050,
      lng: -123.10850,
      popupText: "Tap & Barrel (Olympic Village)",
    },
    {
      id: "parallel-49-brewing",
      lat: 49.28100,
      lng: -123.06500,
      popupText: "Parallel 49 Brewing",
    },
    // Fun Activities
    {
      id: "granville-island",
      lat: 49.27262,
      lng: -123.13524,
      popupText: "Granville Island",
    },
    {
      id: "science-world",
      lat: 49.27325,
      lng: -123.10377,
      popupText: "Science World",
    },
    {
      id: "vancouver-lookout",
      lat: 49.28460,
      lng: -123.11230,
      popupText: "Vancouver Lookout at Harbour Centre",
    },
    // Adventurous Outings
    {
      id: "grouse-mountain",
      lat: 49.37956,
      lng: -123.08180,
      popupText: "Grouse Mountain",
    },
    {
      id: "capilano-bridge",
      lat: 49.34286,
      lng: -123.11503,
      popupText: "Capilano Suspension Bridge Park",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Where to Hang Out for a First Date in Vancouver</title>
        <meta
          name="description"
          content="Discover the best first date spots in Vancouver, from romantic waterfront strolls to cozy cafés, fun activities, and breathtaking adventurous outings."
        />
        <meta
          name="keywords"
          content="Vancouver, first date, date ideas, Stanley Park, Granville Island, best date spots"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/where-hangout-for-a-first-date-in-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Where to Hang Out for a First Date in Vancouver"
        />
        <meta
          property="og:description"
          content="A practical guide to Vancouver’s best first date spots, including scenic parks, cozy cafes, breweries, and local attractions."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/where-hangout-for-a-first-date-in-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/where-hangout-for-a-first-date-in-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Where to Hang Out for a First Date in Vancouver"
        />
        <meta
          name="twitter:description"
          content="A practical guide to Vancouver’s best first date spots, from scenic vistas to cozy cafes."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/where-hangout-for-a-first-date-in-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/where-hangout-for-a-first-date-in-vancouver.jpg"
              }
              alt="Romantic Vancouver waterfront"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro" ref={(el) => setSpotRef("intro", el)}>
              Introduction
            </h2>
            <p>
              Vancouver is one of the most scenic and diverse cities in Canada,
              offering a mix of majestic mountains, urban coastline, and a
              lively food and arts scene. It’s an ideal place to plan an
              unforgettable first date. Whether you’re looking for a budget-
              friendly stroll in nature or a more upscale evening out, this
              guide covers a variety of indoor and outdoor spots that will help
              spark chemistry. Read on for romantic viewpoints, cozy cafés, fun
              local bars, cultural attractions, and even adventurous outings
              perfect for breaking the ice.
            </p>

            {/* OUTDOOR SCENIC SPOTS */}
            <h2
              id="outdoor-scenic"
              ref={(el) => setSpotRef("outdoor-scenic", el)}
            >
              Outdoor Scenic Spots (Romantic & Free)
            </h2>
            <p>
              Nothing beats Vancouver’s natural beauty for a first date,
              especially if you want to impress without spending much. Here are
              three popular outdoor locations that are free to explore and
              provide gorgeous backdrops for conversation.
            </p>

            {/* Stanley Park */}
            <p
              id="stanley-park-seawall"
              ref={(el) => setSpotRef("stanley-park-seawall", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Stanley Park Seawall</strong>{" "}
              <Marker
                coordY={49.30000}
                coordX={-123.14000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Start with an easy stroll or bike ride along the 10 km Seawall.
              You’ll see the city skyline, North Shore mountains, and ocean all
              at once. Pack a picnic or stop by the local cafés in the park for
              a drink. The scenic views and relaxed atmosphere make it a
              stress-free way to get to know each other.
            </p>

            {/* Queen Elizabeth Park */}
            <p
              id="queen-elizabeth-park"
              ref={(el) => setSpotRef("queen-elizabeth-park", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Queen Elizabeth Park</strong>{" "}
              <Marker
                coordY={49.24192}
                coordX={-123.11262}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Head to the city’s highest point for panoramic views. The park’s
              beautifully landscaped quarry gardens and the Bloedel Conservatory
              (a small fee) make for a romantic environment. You can take a
              leisurely walk, enjoy the vibrant flora, and if you’re feeling
              fancy, grab a reservation at Seasons in the Park for dinner with a
              skyline view.
            </p>

            {/* Spanish Banks */}
            <p
              id="spanish-banks"
              ref={(el) => setSpotRef("spanish-banks", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Spanish Banks Beach Park</strong>{" "}
              <Marker
                coordY={49.27694}
                coordX={-123.21611}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – For a quiet beach walk with stunning sunset views, Spanish Banks
              is an excellent choice. Enjoy the expansive shoreline, watch
              windsurfers in the distance, and admire the city skyline across
              the water. It’s a more secluded spot than the bustling English Bay
              or Kits Beach.
            </p>

            {/* COZY CAFÉS & DESSERTS */}
            <h2 id="cozy-cafes" ref={(el) => setSpotRef("cozy-cafes", el)}>
              Cozy Cafés & Dessert Spots (Casual & Budget-Friendly)
            </h2>
            <p>
              If you prefer an easygoing, conversational date in a warm indoor
              setting, Vancouver’s café scene won’t disappoint. Here are two
              local favorites where you can linger over coffee, pastries, or
              ice cream.
            </p>

            {/* 49th Parallel */}
            <p
              id="49th-parallel-cafe"
              ref={(el) => setSpotRef("49th-parallel-cafe", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>49th Parallel Café &amp; Lucky’s Doughnuts</strong>{" "}
              <Marker
                coordY={49.25850}
                coordX={-123.10150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Nestled in Mount Pleasant, this hip café offers top-quality
              coffee and gourmet doughnuts in inventive flavors. The laid-back
              vibe is perfect if you want a low-pressure first date spot. Walk
              around the surrounding neighborhood afterward to check out street
              art and unique boutiques.
            </p>

            {/* Rain or Shine */}
            <p
              id="rain-or-shine"
              ref={(el) => setSpotRef("rain-or-shine", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Rain or Shine Ice Cream</strong>{" "}
              <Marker
                coordY={49.26821}
                coordX={-123.15568}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Located in Kitsilano, it’s a playful destination for trying
              farm-to-cone flavors like Honey Lavender or London Fog. If the
              weather cooperates, you can stroll down to Kits Beach after
              grabbing a cone and watch the sunset over the water.
            </p>

            {/* CASUAL BARS & BREWERIES */}
            <h2 id="casual-bars" ref={(el) => setSpotRef("casual-bars", el)}>
              Casual Bars & Breweries (Laid-Back Evening Vibes)
            </h2>
            <p>
              For a relaxed nighttime option, consider these local craft beer
              and pub-style favorites. They’re ideal for easy conversation over
              a pint or two.
            </p>

            {/* Tap & Barrel */}
            <p
              id="tap-and-barrel-olympic"
              ref={(el) => setSpotRef("tap-and-barrel-olympic", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Tap &amp; Barrel (Olympic Village)</strong>{" "}
              <Marker
                coordY={49.27050}
                coordX={-123.10850}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Enjoy a wide selection of local craft beers, BC wines, and a
              scenic patio overlooking False Creek. It’s a buzzing spot that
              still allows for easy conversation, especially if you snag an
              outdoor table. After drinks, walk along the seawall for a
              waterfront night view of the city lights.
            </p>

            {/* Parallel 49 Brewing */}
            <p
              id="parallel-49-brewing"
              ref={(el) => setSpotRef("parallel-49-brewing", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Parallel 49 Brewing Tasting Room</strong>{" "}
              <Marker
                coordY={49.28100}
                coordX={-123.06500}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Head to East Vancouver for a flight of creative craft brews in a
              bright, mural-filled tasting room. It’s a casual space that
              encourages lighthearted fun and conversation. They often have food
              trucks or a pop-up kitchen on-site if you get hungry.
            </p>

            {/* FUN ACTIVITIES & ATTRACTIONS */}
            <h2
              id="fun-activities"
              ref={(el) => setSpotRef("fun-activities", el)}
            >
              Fun Activities &amp; Attractions (Interactive Dates)
            </h2>
            <p>
              Want to do something more than just sit and chat? Vancouver’s got
              you covered with interactive spots that can spark conversation and
              shared laughter.
            </p>

            {/* Granville Island */}
            <p
              id="granville-island"
              ref={(el) => setSpotRef("granville-island", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Granville Island</strong>{" "}
              <Marker
                coordY={49.27262}
                coordX={-123.13524}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – A staple of Vancouver culture, Granville Island offers an indoor
              public market full of local food stalls, artisan shops, street
              performers, and the option to hop on a tiny ferry across False
              Creek. It’s lively yet laid-back, providing endless conversation
              starters around every corner.
            </p>

            {/* Science World */}
            <p
              id="science-world"
              ref={(el) => setSpotRef("science-world", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Science World</strong>{" "}
              <Marker
                coordY={49.27325}
                coordX={-123.10377}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – An interactive science center inside an iconic geodesic dome.
              You can explore hands-on exhibits and watch live demos. Look for
              their “After Dark” adult nights, where you can sip a drink while
              playing with the exhibits minus the daytime crowds of families.
            </p>

            {/* Vancouver Lookout */}
            <p
              id="vancouver-lookout"
              ref={(el) => setSpotRef("vancouver-lookout", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Vancouver Lookout at Harbour Centre</strong>{" "}
              <Marker
                coordY={49.28460}
                coordX={-123.11230}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Take the glass elevator up for a 360° panorama of the city,
              mountains, and ocean. It’s a memorable experience that instantly
              provides a “wow” moment, especially at sunset or after dark when
              the city lights twinkle below.
            </p>

            {/* ADVENTUROUS OUTINGS */}
            <h2
              id="adventurous-outings"
              ref={(el) => setSpotRef("adventurous-outings", el)}
            >
              Adventurous Outings (For the Active Duo)
            </h2>
            <p>
              If you and your date are feeling bold or want to bond over a
              thrilling experience, these iconic Vancouver attractions will do
              the trick.
            </p>

            {/* Grouse Mountain */}
            <p
              id="grouse-mountain"
              ref={(el) => setSpotRef("grouse-mountain", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Grouse Mountain</strong>{" "}
              <Marker
                coordY={49.37956}
                coordX={-123.08180}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Take the Skyride gondola to the top for stunning views, or if
              you’re fit, tackle the “Grouse Grind.” At the summit, enjoy
              shows, wildlife habitats (including grizzly bears in summer),
              restaurants, and more. It’s a higher-priced excursion but
              incredibly rewarding, especially if you both love the outdoors.
            </p>

            {/* Capilano Suspension Bridge */}
            <p
              id="capilano-bridge"
              ref={(el) => setSpotRef("capilano-bridge", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Capilano Suspension Bridge Park</strong>{" "}
              <Marker
                coordY={49.34286}
                coordX={-123.11503}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />{" "}
              – Walk across a 140m-long bridge suspended high above the
              Capilano River. Explore Treetops Adventure and the Cliffwalk, all
              surrounded by lush rainforest. It’s a bit pricey but unforgettable
              if you and your date are up for mild thrills and scenic wonders.
            </p>

            {/* LOCAL REGULATIONS */}
            <h2
              id="local-regulations"
              ref={(el) => setSpotRef("local-regulations", el)}
            >
              Local Regulations &amp; Best Practices
            </h2>
            <p>
              Vancouver’s public parks and beaches generally prohibit alcohol,
              smoking, and open fires without special permits. If you’re heading
              to a restaurant or bar, bring proper ID (19+). For outdoor spots
              like Grouse Mountain or the Seawall, dress for changing weather
              and follow posted guidelines or hours. Many parks have pay parking
              that closes after 10pm, so check signs to avoid towing. Always
              respect the environment: pack out your garbage, use recycling
              bins, and stay on marked paths.
            </p>

            {/* FAQ */}
            <h2 id="faq" ref={(el) => setSpotRef("faq", el)}>
              FAQ
            </h2>
            <div className="faq-section">
              <h3>1. What are some free first date ideas in Vancouver?</h3>
              <p>
                Walking the Stanley Park Seawall, relaxing on Spanish Banks, or
                exploring Granville Island’s Public Market are all excellent,
                no-cost ways to spend time together while enjoying Vancouver’s
                natural and cultural highlights.
              </p>

              <h3>2. How can I get around if I don’t have a car?</h3>
              <p>
                Vancouver’s public transit (buses, SkyTrain, SeaBus) is quite
                efficient. Places like Stanley Park and downtown attractions are
                easily accessible. You can also rely on rideshares or cabs at
                night if buses or trains are less frequent.
              </p>

              <h3>3. What if it rains?</h3>
              <p>
                Vancouverites are used to rain! If the forecast is wet, pivot to
                cozy cafés, the indoor public market at Granville Island, or
                Science World. A light drizzle doesn’t stop people from enjoying
                a walk with umbrellas either.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion" ref={(el) => setSpotRef("conclusion", el)}>
              Conclusion
            </h2>
            <p>
              Vancouver’s blend of natural beauty and urban charm offers
              countless ways to enjoy a first date. Whether you’re strolling
              through scenic parks, savoring local desserts, sampling craft
              beers, exploring interactive museums, or taking on high-flying
              adventures, each experience can help break the ice and create
              lasting memories. Wherever you choose, remember to stay flexible,
              be present, and let the city’s vibrant atmosphere set the stage
              for a fantastic time.
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}